import { ampli } from 'lib-shared-tracking';
import { useState } from 'react';

import { useAppDispatch } from 'utils/redux/useAppDispatch';

import Image from '../../../../../shared/Image/Image';
import { actions } from '../../../../../shared/attachmentsPreviewModal/actions/attachmentsPreviewModal';
import { AttachedFile } from '../AttachedFile';

import styles from './inlinedImage.module.scss';

const { attachmentsPreviewModalOpen } = actions;

type InlinedImageProps = {
  /** The image src. */
  src: string;
  /** The image alt text. */
  alt?: string;
  /** A number to use as part of the file name. */
  number?: number;
  /**
   * Whether or not to use a fallback in case the image cannot be loaded. In
   * this case we present it with the `AttachedFile` component.
   */
  attachedFileFallback?: boolean;
  /** All images in the message for navigation */
  allImages?: Array<{ url: string; pretty_name: string }>;
};

export const InlinedImage = (props: InlinedImageProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { alt, number, src, attachedFileFallback, allImages } = props;
  const [showFallback, setShowFallback] = useState(false);
  const [url, setUrl] = useState<string>();

  const handleOnClick = () => {
    if (url) {
      // Create array of all images for navigation
      const allImageAttachments = allImages?.map((img) => ({
        fileName: img.pretty_name || 'inlined.jpg',
        url: img.url,
      })) || [{ fileName: 'inlined.jpg', url }];

      // Find current image index
      const currentIndex = allImageAttachments.findIndex(
        (img) => img.url === url,
      );

      dispatch(
        attachmentsPreviewModalOpen({
          fileName: 'inlined.jpg',
          url,
          attachments: allImageAttachments,
          currentIndex: currentIndex >= 0 ? currentIndex : 0,
        }),
      );
      ampli.conversationAttachedImageIsViewed();
    }
  };

  const handleOnLoad = (url: string) => {
    setUrl(url);
  };

  const handleError = (error: string) => {
    if (attachedFileFallback) {
      setShowFallback(true);
    }
  };

  return (
    <>
      {showFallback ? (
        <AttachedFile
          prettyName={alt ?? `attached_file_${number}`}
          fileUrl={src}
          allAttachments={allImages?.map((img) => ({
            fileName: img.pretty_name || 'inlined.jpg',
            url: img.url,
          }))}
        />
      ) : (
        <div className={styles.inlinedImage} onClick={handleOnClick}>
          <Image
            className={styles.image}
            onErrorHandler={handleError}
            onLoad={handleOnLoad}
            alt={alt}
            src={src}
          />
        </div>
      )}
    </>
  );
};
